import type { CSSProperties } from 'react';

export type MarginMode = 'cross' | 'isolated';

export type ExchangeMarginConfig = {
  marginType?: string;
  crossLeverage?: string;
  isolatedLongLeverage?: string;
  isolatedShortLeverage?: string;
} | null;

export type MarginMetrics = {
  equity?: number | null;
  available?: number | null;
  availableMargin?: number | null;
  usedMargin?: number | null;
  maintenanceMargin?: number | null;
  unrealizedPnl?: number | null;
  marginRatio?: number | null;
  /** Sum |notional| / equity — how hard positions are levered now. */
  effectiveLeverage?: number | null;
};

type Props = {
  mode: MarginMode;
  leverage: string;
  exchange: ExchangeMarginConfig;
  busy?: boolean;
  /** Only blocks Apply on exchange — Cross/Isolated/leverage stay selectable. */
  applyDisabled?: boolean;
  metrics?: MarginMetrics | null;
  onMode: (m: MarginMode) => void;
  onLeverage: (v: string) => void;
  onApply: () => void;
  labels?: {
    title?: string;
    exchange?: string;
    apply?: string;
    cross?: string;
    isolated?: string;
    leverage?: string;
    available?: string;
    used?: string;
    maintenance?: string;
    equity?: string;
    effectiveLev?: string;
    ratio?: string;
  };
};

const parseExchange = (cfg: ExchangeMarginConfig) => {
  if (!cfg) return { mode: null as MarginMode | null, lev: null as string | null };
  const mt = String(cfg.marginType || '').toUpperCase();
  const mode: MarginMode | null = mt === 'ISOLATED' ? 'isolated' : mt === 'CROSSED' ? 'cross' : null;
  const lev = mt === 'ISOLATED'
    ? (cfg.isolatedLongLeverage || cfg.isolatedShortLeverage || null)
    : (cfg.crossLeverage || null);
  return { mode, lev };
};

const fmt = (n: number | null | undefined, d = 2) => {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: 0 });
};

export default function MarginControls({
  mode, leverage, exchange, busy, applyDisabled, metrics, onMode, onLeverage, onApply, labels = {},
}: Props) {
  const ex = parseExchange(exchange);
  const L = {
    title: labels.title || 'Margin',
    exchange: labels.exchange || 'On exchange',
    apply: labels.apply || 'Apply on exchange',
    cross: labels.cross || 'Cross',
    isolated: labels.isolated || 'Isolated',
    leverage: labels.leverage || 'Leverage',
    available: labels.available || 'Available',
    used: labels.used || 'Used margin',
    maintenance: labels.maintenance || 'Maint. margin',
    equity: labels.equity || 'Equity',
    effectiveLev: labels.effectiveLev || 'Position leverage',
    ratio: labels.ratio || 'Margin ratio',
  };
  const applyOff = Boolean(applyDisabled || busy);
  const m = metrics || null;

  return (
    <div style={{
      marginTop: 10, padding: 10, borderRadius: 10,
      border: '1px solid var(--line)', background: 'var(--panel-2)',
    }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>{L.title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <button
          type="button"
          disabled={busy}
          onClick={() => onMode('cross')}
          style={{
            ...btn,
            borderColor: mode === 'cross' ? 'var(--gold)' : 'var(--line)',
            color: mode === 'cross' ? 'var(--gold)' : 'var(--text)',
            background: mode === 'cross' ? 'rgba(212,175,55,0.12)' : 'var(--panel)',
          }}
        >{L.cross}</button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onMode('isolated')}
          style={{
            ...btn,
            borderColor: mode === 'isolated' ? 'var(--gold)' : 'var(--line)',
            color: mode === 'isolated' ? 'var(--gold)' : 'var(--text)',
            background: mode === 'isolated' ? 'rgba(212,175,55,0.12)' : 'var(--panel)',
          }}
        >{L.isolated}</button>
      </div>
      <label style={{ display: 'block', marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>{L.leverage}</label>
      <input
        type="number"
        min={1}
        max={125}
        step={1}
        value={leverage}
        disabled={busy}
        onChange={(e) => onLeverage(e.target.value)}
        style={inp}
      />
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', lineHeight: 1.45 }}>
        {L.exchange}:{' '}
        {ex.mode
          ? <span style={{ color: 'var(--gold)' }}>{ex.mode === 'cross' ? L.cross : L.isolated} · {ex.lev || '—'}x</span>
          : <span>—</span>}
      </div>
      {m ? (
        <div style={{
          marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--line)',
          fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)', lineHeight: 1.55,
          display: 'grid', gap: 2,
        }}>
          <div>{L.equity}: <span style={{ color: 'var(--text)' }}>{fmt(m.equity)} USDT</span></div>
          <div>{L.available}: <span style={{ color: 'var(--long)' }}>{fmt(m.availableMargin ?? m.available)} USDT</span></div>
          <div>{L.used}: <span style={{ color: 'var(--text)' }}>{fmt(m.usedMargin)} USDT</span></div>
          <div>{L.maintenance}: <span style={{ color: 'var(--text)' }}>{fmt(m.maintenanceMargin)} USDT</span></div>
          <div>
            {L.effectiveLev}:{' '}
            <span style={{ color: 'var(--gold)' }}>
              {m.effectiveLeverage != null && Number.isFinite(m.effectiveLeverage)
                ? `${m.effectiveLeverage.toFixed(2)}x`
                : '—'}
            </span>
          </div>
          {m.marginRatio != null && Number.isFinite(m.marginRatio) ? (
            <div>{L.ratio}: <span style={{ color: 'var(--text)' }}>{(m.marginRatio * (m.marginRatio > 1 ? 1 : 100)).toFixed(2)}%</span></div>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        disabled={applyOff}
        onClick={onApply}
        title={applyDisabled ? 'API key required' : undefined}
        style={{ ...btn, width: '100%', marginTop: 8, opacity: applyOff ? 0.55 : 1 }}
      >
        {busy ? '…' : L.apply}
      </button>
    </div>
  );
}

const btn: CSSProperties = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)',
  background: 'var(--panel)', color: 'var(--text)', cursor: 'pointer', fontSize: 12,
};
const inp: CSSProperties = {
  width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--text)',
};
